import { Fragment, type ReactElement, type ReactNode } from 'react';

// URLs http(s):// ou começando com www. (link "nu", sem markdown).
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
// Pontuação final que normalmente não faz parte da URL (fim de frase).
const TRAILING_RE = /[.,!?;:)\]}'"]+$/;

/**
 * Renderiza um texto puro transformando URLs em links clicáveis que abrem em
 * nova aba. Usado no chat do time (/admin/conversas) e no chat widget (mensagens
 * do usuário). Preserva o texto ao redor; quem chama controla whitespace-pre-wrap.
 */
export function Linkify({
  text,
  linkClassName = 'underline underline-offset-2 hover:opacity-80',
}: {
  text: string;
  linkClassName?: string;
}): ReactElement {
  const nodes: ReactNode[] = [];
  const re = new RegExp(URL_RE.source, 'gi');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const raw = match[0];
    const trailing = raw.match(TRAILING_RE)?.[0] ?? '';
    const url = trailing ? raw.slice(0, raw.length - trailing.length) : raw;
    const href = url.startsWith('http') ? url : `https://${url}`;
    nodes.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {url}
      </a>,
    );
    if (trailing) nodes.push(trailing);
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

  return (
    <>
      {nodes.map((node, i) => (
        <Fragment key={i}>{node}</Fragment>
      ))}
    </>
  );
}
